import { Box, Flex, Button, Heading, Divider, Tooltip } from '@chakra-ui/core';
import { ArrowBackIcon } from '@chakra-ui/icons';
import React from 'react';
import { RiUserStarLine, RiStackLine } from 'react-icons/ri';
import _uniqBy from 'lodash/uniqBy';
import _union from 'lodash/union';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import PositionsTab from '../administration/recruiterCalendar/PositionsTab';
import RecruiterTab from '../administration/recruiterCalendar/RecruiterTab';
import { CalendarSidebarTabTypeEnum, RecruiterCalendarSidebarTabTypeEnum } from './CalendarSidebarTab';
import { ScheduleSidebarView } from './scheduleSidebar/ScheduleSidebarView';
import { BusinessPositionT } from './types';
import colors from '../../styles/colors';
import { AppointmentAvailability, AvailableBookingWithPosition } from '../../firebase/firestore/documents/appointmentAvaiability';
import { useStoreState } from '../../models/hooks';
import { RemoteConfigKey } from '../../firebase/remoteConfig';
import { useCurrentUserProfile } from '../../app/hooks/useCurrentUserProfile';
import { Recruiter } from '../../firebase/firestore/documents/recruiter';
import useAccessibleAccounts from '../messaging/seeker/candidate/notes/useAccessibleAccounts';
import { CalendarEventType } from './CalendarEventType';
import { EventActionType } from './scheduleList/EventActionTypeEnum';
import { CalendarCreateEvent } from './events/CalendarCreateEvent';
import useEventDetail from './events/useEventDetail';
import { getUndefinedOrEventType } from './getUndefinedOrEventType';
import { CalendarDeleteEvent } from './events/CalendarDeleteEvent';
import { CalendarEventDetail } from './events/CalendarEventDetail';

export type CalendarSidebarProps = {
  recruiters: Recruiter[];

  administrationCalendar: boolean;
  handleCheckedRecruiters?: (recruiterIds: any) => void;
  checkedRecruiters?: string[];
  handleCheckedPositions?: (positionIds: BusinessPositionT[]) => void;

  activeTab?: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum;
  setActiveTab?: (activeTab: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum) => void;
  activeDate: Date;

  activeAvailability: AppointmentAvailability | undefined;
  setIsAppointmentDrawerOpen: (isAppointmentDrawerOpen: boolean) => void;
  setSelectedSchedule: (schedule: AppointmentAvailability | undefined) => void;
  activeCurrentDateRange: {
    fromDate: Date;
    toDate: Date;
  };
};

const CalendarSidebar = React.memo(function CalendarSidebar({
  recruiters,
  administrationCalendar,
  handleCheckedRecruiters,
  checkedRecruiters,
  handleCheckedPositions,
  activeTab,
  setActiveTab,
  activeDate,
  activeAvailability,
  setIsAppointmentDrawerOpen,
  setSelectedSchedule,
  activeCurrentDateRange,
}: CalendarSidebarProps) {
  const { t } = useTranslation('calendar');
  const history: any = useHistory();
  const accessibleAccount = useAccessibleAccounts();
  const { calendarPresenter, groupEventPresenter: calendarGroupEventsPresenter } = useStoreState((s) => s.calendar);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isEventDeleteAlertDialogOpen, setIsEventDeleteAlertDialogOpen] = React.useState<boolean>(false);
  const [isEventDetailDrawerOpen, setIsEventDetailDrawerOpen] = React.useState<boolean>(false);
  const [selectedEventForDetail, setSelectedEventForDetail] = React.useState<CalendarEventType | undefined>(undefined);
  const selectedEvent = useEventDetail(getUndefinedOrEventType(selectedEventForDetail));

  const mergedRecruiters = React.useMemo(() => _uniqBy(_union(recruiters, accessibleAccount), 'id'), [
    recruiters,
    accessibleAccount,
  ]);
  const bookings: AvailableBookingWithPosition[] = React.useMemo(
    () => calendarPresenter.listBooking(activeAvailability?.id ?? activeDate),
    [calendarPresenter, activeAvailability, activeDate],
  );
  const groupEvents = React.useMemo(() => calendarGroupEventsPresenter.listGroupEventByDate(activeDate), [
    calendarGroupEventsPresenter,
    activeDate,
  ]);

  const { currentUserProfile } = useCurrentUserProfile();

  const isPositionFilterOnSidebarEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_POSITION_FILTER_ON_SIDEBAR_OF_ADMINISTRATION_APPOINTMENT_CALENDAR];
  });

  const recruitersColors = ['blue', 'red', 'yellow', 'orange', 'green', 'teal', 'cyan', 'purple', 'pink', 'gray'];

  const moveCurrentUserIdToFirstIndex = (arr: Recruiter[], fromIndex: number, toIndex: number): Recruiter[] => {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
    return arr;
  };
  const getStyleForAvailabilityByUser = React.useCallback(
    (userId: string) => {
      if (administrationCalendar && mergedRecruiters.length > 0) {
        const indexOfCurrentUser = mergedRecruiters.findIndex((r) => r.id === currentUserProfile?.id) % 10;
        if (indexOfCurrentUser < 0) {
          return recruitersColors[0];
        }
        const reOrderedRecruiter = moveCurrentUserIdToFirstIndex(mergedRecruiters, indexOfCurrentUser, 0);
        const indexOfRecruiter = reOrderedRecruiter.findIndex((r) => r.id === userId) % 10;
        return recruitersColors[indexOfRecruiter];
      }
      return recruitersColors[0];
    },
    // eslint-disable-next-line
    [administrationCalendar, mergedRecruiters, currentUserProfile?.id],
  );

  const openAppointmentDrawerCreateBooking = React.useCallback(() => {
    setSelectedSchedule(undefined);
    setIsAppointmentDrawerOpen(true);
  }, [setSelectedSchedule, setIsAppointmentDrawerOpen]);

  const handleCreateNewEvent = React.useCallback(() => {
    setSelectedEventForDetail(undefined);
    setIsOpen(true);
  }, []);

  const handleEvent = React.useCallback((e: CalendarEventType, action: string) => {
    setSelectedEventForDetail(e);
    if (action === EventActionType.edit) {
      setIsOpen(true);
    }
    if (action === EventActionType.delete) {
      setIsEventDeleteAlertDialogOpen(true);
    }
    if (action === EventActionType.view) {
      setIsEventDetailDrawerOpen(true);
    }
  }, []);

  return (
    <React.Fragment>
      {isOpen && (
        <CalendarCreateEvent
          calendarEvent={selectedEventForDetail}
          selectedEvent={selectedEvent}
          isOpen={isOpen}
          onClose={
            // istanbul ignore next
            () => setIsOpen(false)
          }
          allRecruiters={mergedRecruiters}
        />
      )}
      {
        // istanbul ignore next
        isEventDeleteAlertDialogOpen && (
          <CalendarDeleteEvent
            isOpen={isEventDeleteAlertDialogOpen}
            onClose={() => setIsEventDeleteAlertDialogOpen(false)}
            selectedEvent={selectedEvent}
          />
        )
      }

      {
        // istanbul ignore next
        isEventDetailDrawerOpen && (
          <CalendarEventDetail
            selectedEvent={selectedEvent}
            isOpen={isEventDetailDrawerOpen}
            onClose={() => setIsEventDetailDrawerOpen(false)}
          />
        )
      }
      <Box className="calendar-sidebar" w="33%" maxW="325px" minW="225px" borderWidth="1px" borderColor="gray.50">
        {administrationCalendar && handleCheckedRecruiters && handleCheckedPositions && setActiveTab ? (
          <Box>
            <Flex px={4} py={3} justifyContent="space-between" borderBottomWidth="1px" borderBottomColor="gray.50">
              <Button
                _focus={{ boxShadow: 'none' }}
                colorScheme="blue.default"
                color={colors.blue.default}
                variant="ghost"
                fontSize="sm"
                data-testid="BackBtn"
                pl={0}
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
                  history.push({ pathname: '/administration' });
                }}
                leftIcon={<ArrowBackIcon />}
              >
                {t('administration:calendar.backBtnText')}
              </Button>
              <Button
                _focus={{ boxShadow: 'none' }}
                colorScheme="blue.default"
                color={colors.blue.default}
                fontSize="sm"
                variant="outline"
                borderRadius={3}
                onClick={openAppointmentDrawerCreateBooking}
                data-testid="OpenDrawerBtn"
              >
                {t('availabilityForm.myAvailabilityLabel')}
              </Button>
            </Flex>
            <Flex>
              <Heading
                cursor="pointer"
                d="flex"
                flex={1}
                alignItems="flex-start"
                as="h4"
                size="sm"
                px={4}
                py={3}
                borderBottomWidth="2px"
                borderColor={
                  activeTab === CalendarSidebarTabTypeEnum.RECRUITERS && isPositionFilterOnSidebarEnabled ? 'blue.500' : 'gray.50'
                }
                color={
                  activeTab === CalendarSidebarTabTypeEnum.RECRUITERS && isPositionFilterOnSidebarEnabled
                    ? 'blue.500'
                    : 'gray.600'
                }
                data-testid="RecruiterTabHeader"
                onClick={() => {
                  setActiveTab(CalendarSidebarTabTypeEnum.RECRUITERS);
                  handleCheckedRecruiters([currentUserProfile?.id]);
                }}
              >
                <Box as="span" mr={2}>
                  <RiUserStarLine style={{ fontSize: '18px' }} />
                </Box>
                <Box as="span" lineHeight="1.1">
                  {t('administration:calendar.sidebarTitle')}
                </Box>
              </Heading>
              {isPositionFilterOnSidebarEnabled && (
                <Tooltip label={t('calendar:availabilityForm.myPositionsToolTip')} placement="right-end">
                  <Heading
                    cursor="pointer"
                    d="flex"
                    flex={1}
                    alignItems="flex-start"
                    as="h4"
                    size="sm"
                    px={4}
                    py={3}
                    borderBottomWidth="2px"
                    borderColor={
                      activeTab !== CalendarSidebarTabTypeEnum.RECRUITERS && isPositionFilterOnSidebarEnabled
                        ? 'blue.500'
                        : 'gray.50'
                    }
                    color={
                      activeTab !== CalendarSidebarTabTypeEnum.RECRUITERS && isPositionFilterOnSidebarEnabled
                        ? 'blue.500'
                        : 'gray.600'
                    }
                    onClick={() => {
                      setActiveTab(CalendarSidebarTabTypeEnum.FOLLOWED_POSITIONS);
                      handleCheckedRecruiters([currentUserProfile?.id]);
                    }}
                    data-testid="PositionTabHeader"
                  >
                    <Box alignItems="center" lineHeight="1.1">
                      <Flex>
                        <Box as="span" mr={2}>
                          <RiStackLine style={{ fontSize: '18px' }} />
                        </Box>
                        <Box as="span" fontWeight="bold">
                          {t('administration:calendar.positionTitle')}
                        </Box>
                      </Flex>
                    </Box>
                  </Heading>
                </Tooltip>
              )}
            </Flex>
            {activeTab !== CalendarSidebarTabTypeEnum.RECRUITERS && isPositionFilterOnSidebarEnabled && (
              <Box textAlign="right">
                <Flex d="inline-flex" width="50%">
                  <Box
                    px={5}
                    py={1}
                    flex={1}
                    cursor="pointer"
                    fontSize="sm"
                    onClick={() => {
                      setActiveTab(CalendarSidebarTabTypeEnum.FOLLOWED_POSITIONS);
                    }}
                    color={activeTab === CalendarSidebarTabTypeEnum.FOLLOWED_POSITIONS ? 'blue.500' : ''}
                    bg={activeTab === CalendarSidebarTabTypeEnum.FOLLOWED_POSITIONS ? 'blue.40' : ''}
                    borderLeft="1px solid"
                    borderColor="gray.100"
                    data-testid="FollowedPositionMenuBtn"
                  >
                    {t('administration:calendar.followed')}
                  </Box>
                  <Box
                    px={5}
                    py={1}
                    flex={1}
                    cursor="pointer"
                    fontSize="sm"
                    onClick={() => {
                      setActiveTab(CalendarSidebarTabTypeEnum.ALL_POSITIONS);
                    }}
                    color={activeTab === CalendarSidebarTabTypeEnum.ALL_POSITIONS ? 'blue.500' : ''}
                    bg={activeTab === CalendarSidebarTabTypeEnum.ALL_POSITIONS ? 'blue.40' : ''}
                    borderLeft="1px solid"
                    borderColor="gray.100"
                    data-testid="AllPositionMenuBtn"
                  >
                    {t('administration:calendar.all')}
                  </Box>
                </Flex>
                <Divider borderColor="gray.100" />
              </Box>
            )}

            <Divider borderColor="gray.100" />

            {activeTab !== CalendarSidebarTabTypeEnum.RECRUITERS && handleCheckedPositions && (
              <PositionsTab handleCheckedPositions={handleCheckedPositions} listHeight="calc(-330px + 100vh)" />
            )}
            {activeTab === CalendarSidebarTabTypeEnum.RECRUITERS && handleCheckedRecruiters && (
              <RecruiterTab
                clearSearch={
                  /* istanbul ignore next */ () => {
                    if (!!handleCheckedRecruiters && !!handleCheckedPositions) {
                      handleCheckedRecruiters([currentUserProfile?.id]);
                    } else handleCheckedPositions([]);
                  }
                }
                recruiters={mergedRecruiters}
                getStyleForAvailabilityByUser={getStyleForAvailabilityByUser}
                searchText=""
                handleCheckedRecruiters={handleCheckedRecruiters}
                checkedRecruiters={checkedRecruiters}
              />
            )}
          </Box>
        ) : (
          setActiveTab && (
            <ScheduleSidebarView
              eventList={{ bookings, groupEvents, activeDate }}
              createNewEvent={handleCreateNewEvent}
              onOpen={openAppointmentDrawerCreateBooking}
              handleEvent={handleEvent}
              handleCheckedPositions={handleCheckedPositions}
              setActiveTab={setActiveTab}
              activeTab={activeTab as RecruiterCalendarSidebarTabTypeEnum}
              data-testid="SidebarDrawerTest"
              activeCurrentDateRange={activeCurrentDateRange}
            />
          )
        )}
      </Box>
    </React.Fragment>
  );
});
export default CalendarSidebar;
